// LLM generated - Model Claude 4 Sonnet
import { createStyles, Flex, Radio, Stack, useMantineTheme } from '@mantine/core';
import { IconBuildingBank, IconCreditCard } from '@tabler/icons-react';

import { Text } from '~/domains/design-system/Text';
import { PaymentType } from '~/common/enums/payments.enum';
import PaymentTypeIcon from '~/domains/payments/icons/PaymentTypeIcon';
import dt from '~/testing';

const PAYMENT_TYPE_MAP: Record<string, PaymentType> = {
  SKRILL: PaymentType.SKRILL,
  PAYPAL: PaymentType.PAYPAL,
  VENMO: PaymentType.VENMO,
};

const useStyles = createStyles((theme) => ({
  container: {
    width: '100%',
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.white,
    cursor: 'pointer',
    transition: 'all 0.2s ease',

    '&:hover': {
      borderColor: theme.colors.gray[4],
    },
  },
  iconContainer: {
    width: 40,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.white,
  },
  radio: {
    '& input': {
      cursor: 'pointer',
    },
  },
}));

type RefundMethodOptionProps = {
  value: string;
  label: string;
  amount: number;
  isSelected: boolean;
  paymentMethod: string;
  onChange: () => void;
};

export function RefundMethodOption({
  value,
  label,
  amount,
  isSelected,
  paymentMethod,
  onChange,
}: RefundMethodOptionProps) {
  const { classes } = useStyles();
  const theme = useMantineTheme();

  // Get the appropriate icon based on payment method
  const getIcon = () => {
    switch (paymentMethod) {
      case 'AEROPAY':
        return (
          <div className={classes.iconContainer}>
            <IconBuildingBank size={16} color={theme.colors.gray[6]} />
          </div>
        );
      case 'CARD':
        return (
          <div className={classes.iconContainer}>
            <IconCreditCard size={16} color={theme.colors.gray[6]} />
          </div>
        );
      case 'SKRILL':
      case 'PAYPAL':
      case 'VENMO': {
        if (!PAYMENT_TYPE_MAP[paymentMethod]) return null;
        return (
          <div className={classes.iconContainer}>
            <PaymentTypeIcon
              type={PAYMENT_TYPE_MAP[paymentMethod]}
              size={20}
              color={theme.colors.gray[6]}
            />
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div
      data-test-id={dt.payments.withdrawal.components.refundMethodOption.container}
      className={classes.container}
      onClick={onChange}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={theme.other.spacing._12}>
          {getIcon()}
          <Stack spacing={theme.other.spacing._4}>
            <Text
              data-test-id={dt.payments.withdrawal.components.refundMethodOption.text}
              variant="subhead-medium"
              color="gray.9"
              fw={500}
            >
              {label}
            </Text>
            <Text
              data-test-id={dt.payments.withdrawal.components.refundMethodOption.amountText}
              variant="body-small"
              color="gray.6"
            >
              {`$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `}
              available
            </Text>
          </Stack>
        </Flex>

        <Radio checked={isSelected} onChange={onChange} value={value} className={classes.radio} />
      </Flex>
    </div>
  );
}
